/**
 * <<uppercase_module_name>> Module
 */
import Vue from "vue";
import Vuex from "vuex";
import api from "Api";
import router from "Router";
import app from "Main";

// import the api endpoints
import {
    getSqlRelationships,
    getSqlRelationship,
    printSqlRelationship,
    createSqlRelationships,
    updateSqlRelationships,
    deleteSqlRelationships,
} from "Core/configuration/developer/config/api.sql_relationships";

const state = {
    page: {},
    form: {},
    sql_relationships: [],
    sql_relationship_options: [],
    selectedSqlRelationship: {
        relation_id: null,
        company_id: "",
        sql_relationship_date: "",
        reference_number: "",
        status_id: "",
        billing_address_id: "",
        shipping_address_id: "",
        comments: "",
        sql_relationship_details: [],
    },
    sql_relationships_filter: {
        search: "",
        page_size: 2,
        page_num: 1,
    },
    rules_sql_relationships: {
        relation_id: {
            name: "relation_id",
            rules: "trim|max_length[15]|integer",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        field_id: {
            name: "field_id",
            rules: "trim|max_length[15]|integer|required",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        relation_type: {
            name: "relation_type",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        reference_column_id: {
            name: "reference_column_id",
            rules: "trim|max_length[15]|integer|required",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        foreign_object_name: {
            name: "foreign_object_name",
            rules: "trim|max_length[100]|required",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        foreign_model_name: {
            name: "foreign_model_name",
            rules: "trim|max_length[65]|required",
            max_len: "65",
            min_len: "",
            default_value: "",
        },
        is_user_def: {
            name: "is_user_def",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        date_sync: {
            name: "date_sync",
            rules: "trim",
            max_len: "",
            min_len: "",
            default_value: "",
        },
    },
};

// getters
const getters = {
    sql_relationships: (state) => {
        return state.sql_relationships;
    },
};

// actions
const actions = {
    async fetchSqlRelationships({ rootState, commit, dispatch }, payload = null) {
        try {
            let body = payload.body ? payload.body : "";
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            const response = await getSqlRelationships({ body: body, headers: config.headers });

            if (typeof response !== "undefined" && response.data) {
                if (response.data.page) {
                    commit("mutate", {
                        property: "page",
                        with: response.data.page,
                    });
                }

                if (body.options) {
                    commit("mutate", {
                        property: "sql_relationship_options",
                        with: response.data.sql_relationships,
                    });
                } else {
                    commit("mutate", {
                        property: "sql_relationships",
                        with: response.data.sql_relationships,
                    });
                }

                return response.data;
            } else {
                console.log("fetch all response error");
                return;
            }
        } catch (error) {
            console.log("fetch all error");
            return;
        }
    },
    async fetchSqlRelationship({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let body = payload.body ? payload.body : "";
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await getSqlRelationship(payload.id);

            if (typeof response !== "undefined" && response.data) {
                commit("mutate", {
                    property: "selectedSqlRelationship",
                    with: response.data.sql_relationship,
                });
                return response.data;
            } else {
                console.log("fetch response error");
                return;
            }
        } catch (error) {
            console.log("fetch single error");
            return;
        }
    },
    async printSqlRelationship({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await printSqlRelationship(payload.id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("fetch print response error");
                return;
            }
        } catch (error) {
            console.log("print error");
            return;
        }
    },
    async saveSqlRelationship({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            if (typeof body["relation_id"] === "undefined" || body["relation_id"] == null || body["relation_id"] == "") {
                delete body["relation_id"];
                response = await createSqlRelationships({ body: body, headers: config.headers });
            } else {
                let relation_id = body["relation_id"];
                delete body["relation_id"];
                response = await updateSqlRelationships({ body: body, headers: config.headers }, relation_id);
            }

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("save response error");
                return;
            }
        } catch (error) {
            console.log("save error");
            return;
        }
    },
    async deleteSqlRelationship({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await deleteSqlRelationships(payload.id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("delete response error");
                return;
            }
        } catch (error) {
            console.log("delete error");
            return;
        }
    },
};

// mutations
const mutations = {
    mutate(state, payload) {
        state[payload.property] = payload.with;
    },
    resetSqlRelationships(state) {
        state.page = {};
        state.form = {};
        state.sql_relationships = [];
        state.sql_relationship_options = [];
        state.selectedSqlRelationship = {
            relation_id: null,
            company_id: "",
            sql_relationship_date: "",
            reference_number: "",
            status_id: "",
            billing_address_id: "",
            shipping_address_id: "",
            comments: "",
            sql_relationship_details: [],
        };
        state.sql_relationships_filter = {
            search: "",
            page_size: 2,
            page_num: 1,
        };
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
